import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextRenderer from '../common/RichTextRenderer';
import './StudentDashboard.css';
import {
  FaBookOpen,
  FaCheckCircle,
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaCalendar,
  FaMapMarkerAlt,
  FaClock,
  FaEye,
  FaSave,
  FaClipboardList,
  FaFileAlt,
  FaInfoCircle,
  FaStepForward,
  FaDumbbell
} from 'react-icons/fa';
import { FiExternalLink, FiCheck } from 'react-icons/fi';
import {
  startCourse,
  nextContent,
  getProgress,
  saveProgress,
  getNavigationContext,
  bootstrapStudent,
  fetchCourseContent
} from '../../actions/authActions';
import { getEvents } from '../../actions/eventActions';
import { getUserSubmissionStatus } from '../../actions/submissionActions';
import { getCourseContent, calculateCourseProgress, isContentCompleted, getContentTypeLabel, getContentTypeIcon, getAllPathContent, getCourseForContent, isContentNavigable, validateGlobalNavigation } from '../../utils/progressUtils';
import { getStoredCompletion, setStoredCompletion } from './utils/modalHelpers';
import { getUserXP } from '../../actions/xpActions';
import { getTiers } from '../../actions/tierActions';
import EventsSection from './EventsSection';
import CourseCarouselSection from './CourseCarouselSection';
import CourseProgressSection from './CourseProgressSection';
import LoadingStates from './LoadingStates';
import ImageUpload from '../common/ImageUpload';
import SubmissionFormContent from './SubmissionFormContent';
import PathCompletionModal from './PathCompletionModal';
import CourseDetailModal from './CourseDetailModal';
import ContentModalHeader from './ContentModalHeader';
import ModalNavigationButtons from './ModalNavigationButtons';
import LessonContentView from './LessonContentView';
import ReinforcementContentView from './ReinforcementContentView';
import ReinforcementPopup from './ReinforcementPopup';

// Import main dashboard hook
import { useStudentDashboard } from './hooks/useStudentDashboard';


const StudentDashboard = ({
  auth,
  event: { events, loading: eventsLoading },
  xp: { userXP, loading: xpLoading },
  tier: { tiers },
  getEvents,
  startCourse,
  nextContent,
  getProgress,
  saveProgress,
  getNavigationContext,
  bootstrapStudent,
  fetchCourseContent,
  getUserSubmissionStatus,
  getUserXP,
  getTiers
}) => {
  // Use main dashboard hook for all state management
  const {
    // Core state
    user,
    courseDetails,
    userCurrentCourse,
    userCurrentContent,
    currentContentType,
    navigationContext,
    pathCourses,
    userProgress,
    lastAction,
    userSubmissionStatus,

    // Progress state
    pathProgress,
    currentStep,
    courseProgress,
    totalSteps,
    displayedCourse,
    displayedCourseName,

    // Modal state
    showLessonModal,
    selectedCourse,
    setSelectedCourse,
    hasAutoOpenedModal,
    setHasAutoOpenedModal,
    hasShownCompletionModal,
    modalNavigation,
    setModalNavigation,
    currentContentIndex,
    modalCourseContent,

    // Achievement state
    showAchievementView,
    setShowAchievementView,
    hasUserClosedAchievement,
    hasShownAutoCompletion,
    setHasShownAutoCompletion,
    showAchievementConfetti,
    setShowAchievementConfetti,
    hasShownAchievementConfetti,
    setHasShownAchievementConfetti,
    windowDimension,
    expandedLessons,
    setExpandedLessons,
    courseActionLoading,
    setCourseActionLoading,
    nextContentLoading,
    setNextContentLoading,

    // Submission state
    submissionData,
    setSubmissionData,
    urlError,
    setUrlError,
    submissionStatusLoading,
    isSubmitting,
    isWaitingForStatus,
    isSubmitDisabled,
    collaborators,
    setCollaborators,

    // Helper functions
    getCompletedCoursesLocal,
    isCourseCompletedLocal,
    getCourseProgressLocal,
    getCompletedContentForCourseLocal,
    isPathCompletedLocal,
    setShowContentModalWithDebug,
    resetToActualProgressLocal,
    getDisplayedContentLocal,
    getDisplayedContentTypeLocal,
    getDisplayedContentIndexLocal,
    handleBackNavigationLocal,
    handleForwardBrowsingLocal,
    handleCloseLessonModal,
    validateUrlLocal,
    handleUrlChange,
    getCurrentSubmissionStatus,
    canProgressFromSubmission,
    handleSubmitSubmission,
    toggleLessonAccordion,
    handleAchievementClose,

    // Redux actions (only the ones that exist)
    addSubmission,
    updateSubmission
    // Note: Other completion actions are not available in current codebase
  } = useStudentDashboard();

  // Course content lazy-load state
  const [loadingCourseId, setLoadingCourseId] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  // Reinforcement popup state management
  const [showReinforcementPopup, setShowReinforcementPopup] = useState(false);
  const [pendingReinforcementContent, setPendingReinforcementContent] = useState(null);

  // Track which reinforcements have already been shown in this session
  const shownReinforcementsRef = useRef(new Set());

  // Scroll-reset for the lesson modal body. When content swaps (next/previous,
  // submission approval, modal open), the browser preserves scrollTop on the
  // re-rendered scrollable element. Tall prior content → short new content
  // leaves the scroll thumb stuck and the scrollbar visible even when nothing
  // exceeds the viewport. Reset to top on every content change.
  const modalBodyRef = useRef(null);

  // Reset scrollTop when the modal opens or the displayed content changes.
  // Browsers preserve scrollTop across re-renders of the same element, so a tall
  // previous state (a submission form mid-fill) leaves the bar scrolled even
  // after the content collapses to a short approved record.
  useEffect(() => {
    if (!modalBodyRef.current) return;
    modalBodyRef.current.scrollTop = 0;
  }, [
    showLessonModal,
    typeof userCurrentContent === 'object' ? userCurrentContent?._id : userCurrentContent,
    modalNavigation.viewingContent?._id,
    modalNavigation.isViewingBackwards,
    currentContentType
  ]);

  // Auto-show reinforcement popup when modal opens with reinforcement as current content
  useEffect(() => {
    // When modal is open, current content is a reinforcement, and we haven't shown the popup yet
    if (showLessonModal && currentContentType === 'reinforcement' && userCurrentContent && !showReinforcementPopup && !pendingReinforcementContent) {
      const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;

      // Only show popup if we haven't already shown this specific reinforcement
      if (!shownReinforcementsRef.current.has(contentId)) {
        shownReinforcementsRef.current.add(contentId);
        setPendingReinforcementContent(userCurrentContent);
        setShowReinforcementPopup(true);
      }
    }
  }, [showLessonModal, currentContentType, userCurrentContent, showReinforcementPopup, pendingReinforcementContent]);

  // Helper function to calculate content index excluding reinforcements
  const getContentIndexWithoutReinforcements = (course, contentId) => {
    if (!course || !contentId) return 0;

    const courseContent = getCourseContent(course);
    let countWithoutReinforcements = 0;

    for (const item of courseContent) {
      // Check if this item is a reinforcement
      const isReinforcement = course.reinforcements?.some(r => r._id === item._id);

      // If we found our target content, return the count
      if (item._id === contentId) {
        return countWithoutReinforcements;
      }

      // Only increment count for non-reinforcement items
      if (!isReinforcement) {
        countWithoutReinforcements++;
      }
    }

    return countWithoutReinforcements;
  };


  // Calculate current content index without reinforcements
  // When viewing backwards, use the viewing content; otherwise use actual progress
  const currentContentIndexWithoutReinforcements = (() => {
    // When viewing backwards, use the viewing content and course
    if (modalNavigation.isViewingBackwards && modalNavigation.viewingContent && modalNavigation.viewingCourse) {
      return getContentIndexWithoutReinforcements(
        modalNavigation.viewingCourse,
        modalNavigation.viewingContent._id
      );
    }

    // Otherwise, use actual progress position
    if (userCurrentCourse && userCurrentContent) {
      return getContentIndexWithoutReinforcements(
        userCurrentCourse,
        typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent
      );
    }

    return 0;
  })();

  // modalCourseContent is already filtered to exclude reinforcements in useModalNavigation hook
  const totalContentWithoutReinforcements = modalCourseContent.length;

  // Derived state
  const studentPath = user?.path;
  const isLoading = false; // TODO: Handle loading states properly
  const progressLoading = auth.progressLoading;

  // Track bootstrap to only run once per user session
  const bootstrapRef = useRef(null);

  // Bootstrap: load full path/courses/progress data for student (heavy, one-time)
  useEffect(() => {
    const userId = user?.id || user?._id;
    if (user?.role === 'student' && userId && bootstrapRef.current !== userId) {
      bootstrapRef.current = userId;
      bootstrapStudent();
      getUserXP();
      getNavigationContext();
    }
  }, [user, bootstrapStudent, getUserXP, getNavigationContext]);

  // Fetch events when program becomes available (may happen after bootstrap restores full user)
  const eventsLoadedRef = useRef(null);
  useEffect(() => {
    const program = user?.group?.program;
    const programId = typeof program === 'object' && program !== null ? program._id : program;
    if (programId && eventsLoadedRef.current !== programId) {
      eventsLoadedRef.current = programId;
      getEvents(programId);
      getTiers(programId);
    }
  }, [user?.group?.program, getEvents, getTiers]);

  // Check if self-registered user still has placeholder profile values
  const navigate = useNavigate();
  const needsProfileCompletion = user && (
    user.name === 'New' || user.country === 'Not specified'
  );
  const [profileBannerDismissed, setProfileBannerDismissed] = useState(false);

  // Old useEffect hooks removed - now handled by custom hooks

  // Clear next content loading when both Redux progress states are complete
  useEffect(() => {
    if (!auth.progressLoading && !auth.isContentProgressing && nextContentLoading) {
      // Immediately clear local loading state once Redux confirms completion
      setNextContentLoading(false);
    }
  }, [auth.progressLoading, auth.isContentProgressing, nextContentLoading]);

  // Clear course action loading when Redux progress finishes
  useEffect(() => {
    if (!auth.progressLoading && courseActionLoading) {
      setCourseActionLoading(false);
    }
  }, [auth.progressLoading, courseActionLoading]);



  // Determine start button text
  const getStartButtonText = () => {
    if (!userCurrentCourse) return "Start Course";
    if (userCurrentContent) return "JUMP BACK IN";
    return "Start Course";
  };

  // Determine next button text
  const getNextButtonText = () => {
    if (!userCurrentContent) {
      return "Start First Content";
    }

    // Check if this is the last content in the current course
    const isLastContentInCourse = currentContentIndex === modalCourseContent.length - 1;

    if (isLastContentInCourse) {
      // Check if this is the last course in the path
      const currentCourseIndex = pathCourses.findIndex(course => course._id === userCurrentCourse?._id);
      const isLastCourseInPath = currentCourseIndex === pathCourses.length - 1;

      if (isLastCourseInPath) {
        return "Complete Path";
      } else {
        return "Next Course";
      }
    } else {
      return "Next Content";
    }
  };

  // All helper functions are now provided by the hooks

  // Modal auto-open is now handled by useModalNavigation hook

  // Achievement modal tracking is now handled by useAchievementState hook

  // ✅ BUGFIX EFFECTS REMOVED - No longer needed!
  // The backend now always provides valid currentContent and currentContentType,
  // so the modal closing bug is fundamentally solved.

  // Achievement view and confetti are now handled by useAchievementState hook

  // Body scroll management is now handled by useModalNavigation hook




  // Show loading state while essential data is being fetched
  if (!user || isLoading) {
    return <LoadingStates.DashboardLoading />;
  }

  // Handle course start - always open content modal
  const handleStartCourse = () => {
    // Block course start if profile is incomplete
    if (needsProfileCompletion) {
      navigate('/profile?complete=true');
      return;
    }

    const buttonText = getStartButtonText();

    // Reset navigation state to actual progress when opening modal
    resetToActualProgressLocal();

    if (buttonText === "JUMP BACK IN") {
      // User has a current lesson, just open modal
      setShowContentModalWithDebug(true);
    } else {
      // "Start Course" - call API first, then modal will auto-open on success
      setCourseActionLoading(true);
      setHasAutoOpenedModal(false); // Reset flag so modal can auto-open after API call
      startCourse();
    }
  };

  // Handle content progression with peek-ahead for reinforcements
  const handleNextContent = () => {
    const nextButtonText = getNextButtonText();

    // Peek-ahead: Check if next content is reinforcement
    let nextContentItem = null;
    let nextContentTypeValue = null;

    // IMPORTANT: Always check current course first for reinforcements, regardless of button text
    // This ensures we catch reinforcements at the end of courses
    const currentCourseContent = getCourseContent(userCurrentCourse);
    const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;
    const actualCurrentIndex = currentCourseContent.findIndex(c => c._id === contentId);
    const nextContentIndex = actualCurrentIndex + 1;

    // First, check if there's more content in the CURRENT course (including reinforcements)
    if (nextContentIndex < currentCourseContent.length) {
      nextContentItem = currentCourseContent[nextContentIndex];

      // Determine content type by checking which array it belongs to
      const isLesson = userCurrentCourse.lessons?.some(l => l._id === nextContentItem._id);
      const isSubmissionForm = userCurrentCourse.submissionForms?.some(s => s._id === nextContentItem._id);
      const isReinforcement = userCurrentCourse.reinforcements?.some(r => r._id === nextContentItem._id);

      if (isReinforcement) {
        nextContentTypeValue = 'reinforcement';
      } else if (isSubmissionForm) {
        nextContentTypeValue = 'submissionForm';
      } else if (isLesson) {
        nextContentTypeValue = 'lesson';
      }

    } else if (nextButtonText === "Next Course") {
      // Only check next course if we've exhausted current course content
      const currentCourseIndex = pathCourses.findIndex(c => c._id === userCurrentCourse?._id);
      const nextCourseIndex = currentCourseIndex + 1;

      if (nextCourseIndex < pathCourses.length) {
        const nextCourse = pathCourses[nextCourseIndex];
        const nextCourseContent = getCourseContent(nextCourse);

        if (nextCourseContent.length > 0) {
          nextContentItem = nextCourseContent[0];

          // Determine content type
          const isLesson = nextCourse.lessons?.some(l => l._id === nextContentItem._id);
          const isSubmissionForm = nextCourse.submissionForms?.some(s => s._id === nextContentItem._id);
          const isReinforcement = nextCourse.reinforcements?.some(r => r._id === nextContentItem._id);

          if (isReinforcement) {
            nextContentTypeValue = 'reinforcement';
          } else if (isSubmissionForm) {
            nextContentTypeValue = 'submissionForm';
          } else if (isLesson) {
            nextContentTypeValue = 'lesson';
          }

          setHasAutoOpenedModal(false); // Allow auto-open for new course
        }
      }
    } else if (nextButtonText === "Complete Path") {
      // Achievement view will be handled by useEffect when backend confirms completion
    }

    // If next content is reinforcement, show popup WITHOUT calling API
    if (nextContentTypeValue === 'reinforcement') {
      // Show popup directly with the reinforcement
      setPendingReinforcementContent(nextContentItem);
      setShowReinforcementPopup(true);

      // Don't call API, don't change current content
      return;
    }

    // Not a reinforcement - proceed normally with API call
    setNextContentLoading(true);

    // Reset navigation state before making progress update
    resetToActualProgressLocal();

    // Make the API call - backend will provide updated currentContent and currentContentType
    nextContent();
  };

  // handleCloseLessonModal is now provided by the hook

  const handleSaveAndQuit = () => {
    // Prevent the auto-open useEffect from interfering during save operation
    setHasAutoOpenedModal(true);

    // Reset navigation state to actual progress
    resetToActualProgressLocal();

    // Close the modal immediately
    setShowContentModalWithDebug(false);
    setShowAchievementView(false);

    // Then perform the async operations
    saveProgress();
    getProgress();
  };

  // Handle reinforcement popup close with auto-advance
  const handleReinforcementPopupClose = () => {

    // Close the popup UI
    setShowReinforcementPopup(false);
    setPendingReinforcementContent(null);

    // Set loading state
    setNextContentLoading(true);
    resetToActualProgressLocal();

    // Single API call with skipReinforcement flag
    // Backend will automatically skip the reinforcement and return the next content
    nextContent(true);
  };

  // toggleLessonAccordion is now provided by useAchievementState hook

  // Validate URL function
  const validateUrl = (url) => {
    if (!url || url.trim() === '') {
      setUrlError('');
      return true;
    }
    
    try {
      new URL(url.trim());
      setUrlError('');
      return true;
    } catch (error) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
      return false;
    }
  };

  // handleUrlChange is now provided by useSubmissionState hook

  // Handle submission form display
  // Handle submission form submission
  // handleSubmitSubmission is now provided by the hook

  // Show loading while user data is still being fetched, then no-path if truly unassigned
  if (!studentPath) {
    if (auth.isLoading) {
      return <LoadingStates.DashboardLoading />;
    }
    return <LoadingStates.NoPathAssigned />;
  }

  return (
    <div className="min-h-screen bg-background pb-20 lg:pb-0">
      <div className="max-w-6xl mx-auto">

        {/* Profile completion banner for self-registered users */}
        {needsProfileCompletion && !profileBannerDismissed && (
          <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FaInfoCircle className="text-amber-500 shrink-0" />
              <p className="text-sm text-amber-800">
                Please complete your profile information.{' '}
                <button
                  onClick={() => navigate('/profile?complete=true')}
                  className="font-semibold underline hover:text-amber-900"
                >
                  Complete Profile
                </button>
              </p>
            </div>
            <button
              onClick={() => setProfileBannerDismissed(true)}
              className="text-amber-400 hover:text-amber-600 text-lg font-bold shrink-0"
            >
              ✕
            </button>
          </div>
        )}

        {/* Top bar + Hero progress */}
        <CourseProgressSection
          user={user}
          studentPath={studentPath}
          pathCourses={pathCourses}
          userProgress={userProgress}
          pathProgress={pathProgress}
          currentStep={currentStep}
          totalSteps={totalSteps}
          userXP={userXP}
          tiers={tiers}
          onStartCourse={handleStartCourse}
          progressLoading={progressLoading || courseActionLoading}
          needsProfileCompletion={needsProfileCompletion}
        />

        <div className="h-px bg-border mx-5 sm:mx-10" />

        {/* Course Carousel */}
        <CourseCarouselSection
          pathCourses={pathCourses}
          userCurrentCourse={userCurrentCourse}
          userProgress={userProgress}
          isCourseCompleted={isCourseCompletedLocal}
          getCourseProgress={getCourseProgressLocal}
          getCourseContent={getCourseContent}
          navigationContext={navigationContext}
          modalNavigation={modalNavigation}
          onCourseClick={async (course) => {
            setExpandedLessons(new Set());
            const courseId = course._id;
            // Use cached full course if available, otherwise fetch
            const full = courseDetails[courseId] || await (async () => {
              setLoadingCourseId(courseId);
              try { return await fetchCourseContent(courseId); }
              finally { setLoadingCourseId(null); }
            })();
            setSelectedCourse(full);
          }}
          loadingCourseId={loadingCourseId}
          onStartCourse={handleStartCourse}
          progressLoading={progressLoading || courseActionLoading}
          coursesLoading={pathCourses.length === 0}
          getStartButtonText={getStartButtonText}
          needsProfileCompletion={needsProfileCompletion}
        />

        <div className="h-px bg-border mx-5 sm:mx-10" />

        {/* Events */}
        <EventsSection
          events={events}
          eventsLoading={eventsLoading}
          onEventClick={(event) => setSelectedCourse(event)}
        />

        {/* Content Modal */}
        {(() => {
          const shouldShowModal = showLessonModal && (userCurrentContent || modalCourseContent.length > 0 || isPathCompletedLocal() || showAchievementView);
          return shouldShowModal;
        })() && ReactDOM.createPortal(
          <motion.div
            className="fixed inset-0 z-[9999] overflow-hidden"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="bg-white w-full h-full flex flex-col relative overflow-hidden"
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Modal Header */}
              <ContentModalHeader
                showAchievementView={showAchievementView}
                isPathCompletedLocal={isPathCompletedLocal}
                modalNavigation={modalNavigation}
                userCurrentCourse={userCurrentCourse}
                currentContentType={currentContentType}
                studentPath={studentPath}
                userProgress={userProgress}
                pathCourses={pathCourses}
                getCourseProgressLocal={getCourseProgressLocal}
                getCompletedContentForCourseLocal={getCompletedContentForCourseLocal}
                courseProgress={courseProgress}
                modalCourseContent={modalCourseContent}
                onClose={handleCloseLessonModal}
              />

              {/* Lesson Content - scrollable area, takes remaining space */}
              <div
                ref={modalBodyRef}
                className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-8 sm:py-5 lesson-modal-scroll"
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--border)) transparent' }}
              >
                {(showAchievementView || isPathCompletedLocal()) ? (
                  /* Path Completion Congratulations Screen */
                  <PathCompletionModal
                    studentPath={studentPath}
                    pathCoursesLength={pathCourses.length}
                    showConfetti={showAchievementConfetti}
                    windowDimension={windowDimension}
                    onClose={handleCloseLessonModal}
                  />
                ) : currentContentType === 'reinforcement' ? (
                  /* Blank background when reinforcement is current content - popup will overlay */
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-sm">Reinforcement content loading...</p>
                  </div>
                ) : getDisplayedContentLocal() ? (
                  <div>
                    {/* Course Context Breadcrumb for Cross-Course Navigation */}

                    {/* Chapter heading lockup — the signature move */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={getDisplayedContentLocal()._id || getDisplayedContentLocal().title}
                        className="relative mb-3 pb-2 max-w-5xl mx-auto"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={{
                          hidden: {},
                          visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
                          exit: { transition: { staggerChildren: 0.04, staggerDirection: -1 } }
                        }}
                      >
                        {/* Type label band */}
                        <motion.div
                          className="flex items-center justify-between mb-2"
                          variants={{
                            hidden: { opacity: 0, y: 14 },
                            visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
                            exit: { opacity: 0, y: -8, transition: { duration: 0.2 } }
                          }}
                        >
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`inline-block w-1.5 h-1.5 rounded-full ${
                                getDisplayedContentTypeLocal() === 'submissionForm'
                                  ? 'bg-mission'
                                  : 'bg-primary'
                              }`}
                              aria-hidden="true"
                            />
                            <span
                              className={`text-[10px] font-bold uppercase tracking-[0.22em] ${
                                getDisplayedContentTypeLocal() === 'submissionForm'
                                  ? 'text-mission'
                                  : 'text-primary'
                              }`}
                            >
                              {getDisplayedContentTypeLocal() === 'submissionForm' ? 'Mission' : 'Lesson'}
                            </span>
                            {modalNavigation.isViewingBackwards && (
                              <>
                                <span className="text-foreground/25">·</span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-foreground/55">
                                  Reviewing
                                </span>
                              </>
                            )}
                          </div>

                          {getDisplayedContentTypeLocal() === 'lesson' && getDisplayedContentLocal().slug && (
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/lesson/${getDisplayedContentLocal().slug}`;
                                navigator.clipboard.writeText(url);
                                setShareCopied(true);
                                setTimeout(() => setShareCopied(false), 2000);
                              }}
                              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground/55 hover:text-primary transition-colors"
                              title="Copy lesson link"
                            >
                              {shareCopied ? (
                                <><FiCheck className="w-3 h-3" /> Copied</>
                              ) : (
                                <><FiExternalLink className="w-3 h-3" /> Share link</>
                              )}
                            </button>
                          )}
                        </motion.div>

                        {/* Numeral + meta + title lockup */}
                        <div className="flex items-start gap-5 sm:gap-7">
                          {/* The big numeral — signature, lifts in with weight */}
                          <motion.div
                            className="flex-shrink-0 pt-1"
                            variants={{
                              hidden: { opacity: 0, y: 32, scale: 0.82, filter: 'blur(8px)' },
                              visible: {
                                opacity: 1, y: 0, scale: 1, filter: 'blur(0px)',
                                transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
                              },
                              exit: { opacity: 0, y: -10, scale: 0.96, transition: { duration: 0.22 } }
                            }}
                          >
                            <div
                              className="font-display font-bold text-foreground tabular-nums leading-none"
                              style={{
                                fontSize: 'clamp(40px, 6vw, 60px)',
                                letterSpacing: '-0.04em'
                              }}
                            >
                              {String(currentContentIndexWithoutReinforcements + 1).padStart(2, '0')}
                            </div>
                            <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/40 tabular-nums">
                              of {String(totalContentWithoutReinforcements).padStart(2, '0')}
                            </div>
                          </motion.div>

                          {/* Vertical rule — draws itself in like a pen stroke */}
                          <motion.div
                            className="flex-shrink-0 self-stretch w-px bg-border mt-2 origin-top"
                            aria-hidden="true"
                            variants={{
                              hidden: { scaleY: 0, opacity: 0 },
                              visible: { scaleY: 1, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                              exit: { opacity: 0, transition: { duration: 0.2 } }
                            }}
                          />

                          {/* Title — wipes in left-to-right via clip-path */}
                          <motion.div
                            className="min-w-0 flex-1 pt-1"
                            variants={{
                              hidden: { opacity: 0, y: 16, clipPath: 'inset(0 100% 0 0)' },
                              visible: {
                                opacity: 1, y: 0, clipPath: 'inset(0 0% 0 0)',
                                transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] }
                              },
                              exit: { opacity: 0, y: -8, transition: { duration: 0.22 } }
                            }}
                          >
                            <h4
                              className="font-display font-bold text-foreground leading-[1.15]"
                              style={{
                                fontSize: 'clamp(22px, 2.6vw, 30px)',
                                letterSpacing: '-0.02em'
                              }}
                            >
                              {getDisplayedContentLocal().title}
                            </h4>
                          </motion.div>
                        </div>

                        {/* Hairline rule draws across after the lockup settles */}
                        <motion.div
                          className="mt-2 h-px bg-border origin-left"
                          variants={{
                            hidden: { scaleX: 0 },
                            visible: { scaleX: 1, transition: { duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] } },
                            exit: { scaleX: 0, transition: { duration: 0.2 } }
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {getDisplayedContentTypeLocal() === 'submissionForm' ? (
                      <SubmissionFormContent
                        userCurrentContent={getDisplayedContentLocal()}
                        submissionStatus={getCurrentSubmissionStatus()}
                        submissionData={submissionData}
                        setSubmissionData={setSubmissionData}
                        urlError={urlError}
                        submissionStatusLoading={submissionStatusLoading}
                        isSubmitting={isSubmitting}
                        isWaitingForStatus={isWaitingForStatus}
                        isViewingBackwards={modalNavigation.isViewingBackwards}
                        handleUrlChange={handleUrlChange}
                        getCurrentSubmissionStatus={getCurrentSubmissionStatus}
                        handleSubmitSubmission={handleSubmitSubmission}
                        userSubmissionStatus={userSubmissionStatus}
                        collaborators={collaborators}
                        setCollaborators={setCollaborators}
                        tokenConfig={auth.token ? { headers: { Authorization: `Bearer ${auth.token}` } } : {}}
                      />
                    ) : (
                      <LessonContentView
                        isViewingBackwards={modalNavigation.isViewingBackwards}
                        getDisplayedContentLocal={getDisplayedContentLocal}
                      />
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <LoadingStates.LoadingWithMessage message="Please wait while we load the course content." />
                  </div>
                )}
              </div>

              {/* Navigation buttons — pinned at bottom of modal, never scrolls */}
              <ModalNavigationButtons
                showAchievementView={showAchievementView}
                isPathCompletedLocal={isPathCompletedLocal}
                getDisplayedContentLocal={getDisplayedContentLocal}
                modalNavigation={modalNavigation}
                navigationContext={navigationContext}
                handleBackNavigationLocal={handleBackNavigationLocal}
                handleSaveAndQuit={handleSaveAndQuit}
                getCurrentSubmissionStatus={getCurrentSubmissionStatus}
                getDisplayedContentTypeLocal={getDisplayedContentTypeLocal}
                handleSubmitSubmission={handleSubmitSubmission}
                userCurrentContent={userCurrentContent}
                submissionData={submissionData}
                urlError={urlError}
                handleForwardBrowsingLocal={handleForwardBrowsingLocal}
                handleNextContent={handleNextContent}
                nextContentLoading={nextContentLoading}
                progressLoading={auth.progressLoading}
                isContentProgressing={auth.isContentProgressing}
                canProgressFromSubmission={canProgressFromSubmission}
                currentContentIndex={currentContentIndexWithoutReinforcements}
                getNextButtonText={getNextButtonText}
                modalCourseContentLength={totalContentWithoutReinforcements}
                userSubmissionStatus={userSubmissionStatus}
                isSubmitting={isSubmitting}
              />
            </motion.div>
          </motion.div>,
          document.body
        )}

        {/* Course Detail Modal */}
        <CourseDetailModal
          selectedCourse={selectedCourse}
          onClose={() => {
            setExpandedLessons(new Set());
            setSelectedCourse(null);
          }}
          userProgress={userProgress}
          userCurrentContent={userCurrentContent}
          userCurrentCourse={userCurrentCourse}
          courseProgress={courseProgress}
          isCourseCompletedLocal={isCourseCompletedLocal}
          expandedLessons={expandedLessons}
          setExpandedLessons={setExpandedLessons}
          toggleLessonAccordion={toggleLessonAccordion}
          onLessonClick={(content) => {
            // Close detail modal
            setExpandedLessons(new Set());
            setSelectedCourse(null);

            // Find the global index of this content
            const allPathContent = navigationContext?.allPathContent || [];
            const globalIndex = allPathContent.findIndex(c => c._id === content._id);
            const course = getCourseForContent(content._id, pathCourses);

            if (globalIndex === -1 || !course) return;

            // Find local index within the course
            const courseContent = getCourseContent(course);
            const localIndex = courseContent.findIndex(c => c._id === content._id);

            // Set modal navigation to view this content
            setModalNavigation({
              viewingContent: content,
              viewingContentType: content.type,
              viewingIndex: localIndex,
              globalIndex: globalIndex,
              viewingCourse: course,
              isViewingBackwards: true,
              allPathContent: allPathContent,
              canNavigateBack: globalIndex > 0,
              canNavigateForward: true
            });

            // Open the content modal
            setShowContentModalWithDebug(true);
          }}
        />

        {/* Reinforcement Popup */}
        <ReinforcementPopup
          isOpen={showReinforcementPopup}
          content={pendingReinforcementContent}
          onClose={handleReinforcementPopupClose}
        />

      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  event: state.event,
  submission: state.submission,
  xp: state.xp,
  tier: state.tier
});

export default connect(
  mapStateToProps,
  {
    getEvents,
    startCourse,
    nextContent,
    getProgress,
    saveProgress,
    getNavigationContext,
    bootstrapStudent,
    fetchCourseContent,
    getUserSubmissionStatus,
    getUserXP,
    getTiers
  }
)(StudentDashboard);
